import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Heading,
    HStack,
    Input,
    SimpleGrid,
    Stack,
    Table,
    Text,
} from '@chakra-ui/react';
import { EditIcon, PlusIcon, TrashIcon } from '../components/Icons';
import { api, Company } from '../api/api';
import { useConfirmation, useNotification } from '../context/NotificationContext';
import { FormControl, FormErrorMessage, FormLabel } from '../components/FormControls';
import { Panel, PanelBody, PanelHeader } from '../components/Panel';
import SimpleModal from '../components/SimpleModal';
import { useNavigate } from 'react-router-dom';

const CompaniesPage: React.FC = () => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState<Partial<Company>>({ name: '', address: '', contactInfo: '', logoUrl: '' });
    const [editId, setEditId] = useState<number | null>(null);
    const [validation, setValidation] = useState({ name: true });
    const [error, setError] = useState('');
    const [companyCounts, setCompanyCounts] = useState<Record<number, { active: number; deactivated: number }>>({});
    const { notify } = useNotification();
    const { confirm } = useConfirmation();
    const navigate = useNavigate();

    const loadCompanies = () => {
        setError('');
        Promise.all([api.companies.list(), api.systems.list({ includeInactive: true })])
            .then(([companyList, systems]) => {
                setCompanies(companyList);

                const counts: Record<number, { active: number; deactivated: number }> = {};
                systems.forEach(system => {
                    const companyId = system.branch?.company?.id;
                    if (!companyId) return;
                    if (!counts[companyId]) counts[companyId] = { active: 0, deactivated: 0 };
                    if (system.isActive === false) counts[companyId].deactivated += 1;
                    else counts[companyId].active += 1;
                });
                setCompanyCounts(counts);
            })
            .catch(err => setError(err?.message || 'Failed to load companies'));
    };

    useEffect(() => {
        loadCompanies();
    }, []);

    const openCreate = () => {
        setShowForm(true);
        setEditId(null);
        setForm({ name: '', address: '', contactInfo: '', logoUrl: '' });
        setValidation({ name: true });
    };

    const openEdit = (company: Company) => {
        setShowForm(true);
        setEditId(company.id);
        setForm(company);
        setValidation({ name: true });
    };

    const cancelForm = () => {
        setShowForm(false);
        setEditId(null);
        setForm({ name: '', address: '', contactInfo: '', logoUrl: '' });
        setValidation({ name: true });
    };

    const validateForm = () => {
        const next = { name: !!form.name?.trim() };
        setValidation(next);
        return next.name;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        try {
            if (editId == null) {
                await api.companies.create(form as Company);
                notify({ type: 'success', message: 'Company created' });
            } else {
                await api.companies.update(form as Company);
                notify({ type: 'success', message: 'Company updated' });
            }
            cancelForm();
            loadCompanies();
        } catch (err: any) {
            const errorMessage = err?.response?.data?.error || err?.response?.data?.title || err.message || 'Failed to save company';
            notify({ type: 'error', message: errorMessage });
        }
    };

    const handleDelete = (company: Company) => {
        confirm({
            title: 'Delete Company',
            message: `Delete ${company.name}? This cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            confirmColorScheme: 'red',
            onConfirm: async () => {
                try {
                    await api.companies.delete(company.id);
                    notify({ type: 'success', message: 'Company deleted' });
                    loadCompanies();
                } catch (err: any) {
                    const errorMessage = err?.response?.data?.error || err?.response?.data?.title || err.message || 'Delete failed';
                    notify({ type: 'error', message: errorMessage });
                }
            },
        });
    };

    return (
        <Stack gap={6}>
            <HStack justify="space-between" flexWrap="wrap" gap={3}>
                <Heading size="lg">Companies</Heading>
                <Button colorPalette="brand" onClick={openCreate} display="inline-flex" alignItems="center" gap={2}>
                    <PlusIcon />
                    New Company
                </Button>
            </HStack>

            {error && (
                <Alert.Root status="error" borderRadius="md">
                    <Alert.Content>
                        <Alert.Description>{error}</Alert.Description>
                    </Alert.Content>
                </Alert.Root>
            )}

            <Panel shadow="md">
                <PanelHeader>
                    <Heading size="md">Directory</Heading>
                </PanelHeader>
                <PanelBody>
                    <Box overflow="auto" maxH="60vh">
                    <Table.Root>
                        <Table.Header position="sticky" top="0" bg="white" zIndex={1}>
                            <Table.Row>
                                <Table.ColumnHeader>Name</Table.ColumnHeader>
                                <Table.ColumnHeader>Address</Table.ColumnHeader>
                                <Table.ColumnHeader>Contact</Table.ColumnHeader>
                                <Table.ColumnHeader>Logo URL</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="center">Active Machines</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="center">Deactivated Machines</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {companies.map(company => (
                                <Table.Row key={company.id}>
                                    <Table.Cell>{company.name}</Table.Cell>
                                    <Table.Cell>{company.address || '—'}</Table.Cell>
                                    <Table.Cell>{company.contactInfo || '—'}</Table.Cell>
                                    <Table.Cell>
                                        {company.logoUrl ? (
                                            <Text color="brand.500" lineClamp={1} fontWeight="medium">
                                                {company.logoUrl}
                                            </Text>
                                        ) : '—'}
                                    </Table.Cell>
                                    <Table.Cell textAlign="center">{companyCounts[company.id]?.active ?? 0}</Table.Cell>
                                    <Table.Cell textAlign="center">{companyCounts[company.id]?.deactivated ?? 0}</Table.Cell>
                                    <Table.Cell textAlign="right">
                                        <HStack gap={1} justify="flex-end">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                colorPalette="blue"
                                                onClick={() => navigate(`/systems?companyId=${company.id}`)}
                                                title="Machines"
                                            >
                                                Machines
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => openEdit(company)} title="Edit">
                                                <EditIcon />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                colorPalette="red"
                                                onClick={() => handleDelete(company)}
                                                title="Delete"
                                            >
                                                <TrashIcon />
                                            </Button>
                                        </HStack>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>
                    </Box>
                </PanelBody>
            </Panel>

            {showForm && (
                <SimpleModal
                    isOpen={showForm}
                    onClose={cancelForm}
                    title={editId == null ? 'New Company' : 'Edit Company'}
                    size="md"
                    footer={
                        <HStack gap={3} justify="flex-end">
                            <Button variant="ghost" onClick={cancelForm}>
                                Cancel
                            </Button>
                            <Button colorPalette="brand" onClick={handleSubmit}>
                                {editId == null ? 'Create Company' : 'Update Company'}
                            </Button>
                        </HStack>
                    }
                >
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                        <FormControl isRequired isInvalid={!validation.name}>
                            <FormLabel>Company Name</FormLabel>
                            <Input
                                value={form.name || ''}
                                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                            />
                            <FormErrorMessage>Company name is required.</FormErrorMessage>
                        </FormControl>
                        <FormControl>
                            <FormLabel>Logo URL</FormLabel>
                            <Input
                                value={form.logoUrl || ''}
                                onChange={e => setForm(f => ({ ...f, logoUrl: e.target.value }))}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Address</FormLabel>
                            <Input
                                value={form.address || ''}
                                onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Contact Info</FormLabel>
                            <Input
                                value={form.contactInfo || ''}
                                onChange={e => setForm(f => ({ ...f, contactInfo: e.target.value }))}
                            />
                        </FormControl>
                    </SimpleGrid>
                </SimpleModal>
            )}
        </Stack>
    );
};

export default CompaniesPage;
